/**
 * Arborescence site → plateforme → source → mots-clés, avec les zones
 * d'instructions LLM à chaque niveau. L'héritage est rappelé sur place : le
 * niveau le plus spécifique l'emporte.
 */
import { useState } from 'react';
import {
  AUTOMATED_PLATFORMS,
  MANUAL_PLATFORMS,
  type Platform,
  type PlatformKind,
  type Site,
  type Source,
} from '@vigie/shared';
import { api, type CollectionOutcome } from '../api.js';
import { Card, Empty, Field, Notice, PollBadge } from '../components.js';
import { formatDate, useAction, useAsync } from '../hooks.js';

const ALL_KINDS: PlatformKind[] = [...AUTOMATED_PLATFORMS, ...MANUAL_PLATFORMS];

const SOURCE_KIND_FOR: Record<string, string> = {
  reddit: 'subreddit',
  hackernews: 'query',
  stackexchange: 'tag',
  rss: 'rss_feed',
  bluesky: 'query',
  mastodon: 'tag',
  facebook_group: 'group_url',
  linkedin: 'group_url',
  manual: 'topic',
};

const BACKFILL_CAPABLE = ['reddit', 'hackernews', 'stackexchange', 'rss', 'bluesky', 'mastodon'];

export function Config() {
  const sites = useAsync(() => api.sites(), []);
  const { run, busy, error } = useAction();
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');

  const addSite = async () => {
    const created = await run(() => api.createSite({ name, domain }));
    if (created) {
      setName('');
      setDomain('');
      sites.reload();
    }
  };

  return (
    <>
      <header className="topbar">
        <h1>Configuration</h1>
      </header>

      <div className="content">
        {error ? <Notice kind="error">{error}</Notice> : null}
        {sites.error ? <Notice kind="error">{sites.error}</Notice> : null}

        <Card title="Ajouter un site">
          <div className="row">
            <div>
              <label>Nom</label>
              <input value={name} onChange={(event) => setName(event.target.value)} placeholder="CertFleet" />
            </div>
            <div>
              <label>Domaine</label>
              <input
                value={domain}
                onChange={(event) => setDomain(event.target.value)}
                placeholder="certfleet.net"
              />
            </div>
            <div className="shrink">
              <button type="button" className="primary" onClick={addSite} disabled={busy || !name || !domain}>
                Ajouter
              </button>
            </div>
          </div>
        </Card>

        {(sites.data ?? []).map((site) => (
          <SiteNode key={site.id} site={site} onChanged={sites.reload} />
        ))}

        {sites.data && sites.data.length === 0 ? (
          <Card>
            <Empty>Aucun site configuré. Commencez par en ajouter un ci-dessus.</Empty>
          </Card>
        ) : null}
      </div>
    </>
  );
}

function SiteNode({ site, onChanged }: { site: Site; onChanged: () => void }) {
  const [open, setOpen] = useState(true);
  const platforms = useAsync(() => api.platforms(site.id), [site.id]);
  const { run, busy, error } = useAction();
  const [instructions, setInstructions] = useState(site.llmInstructions ?? '');
  const [rescored, setRescored] = useState<string | null>(null);
  const [umamiId, setUmamiId] = useState(site.umamiWebsiteId ?? '');
  const [kind, setKind] = useState<PlatformKind>('reddit');
  const [configValue, setConfigValue] = useState('');

  const saveSite = () =>
    run(() =>
      api.updateSite(site.id, {
        llmInstructions: instructions || null,
        umamiWebsiteId: umamiId || null,
      }),
    );

  const addPlatform = async () => {
    const config: Record<string, unknown> = {};
    if (kind === 'stackexchange') config.site = configValue || 'serverfault';
    if (kind === 'mastodon') config.instance = configValue || 'https://mastodon.social';
    if (kind === 'facebook_group' || kind === 'linkedin') {
      config.reminderEveryDays = Number(configValue) || 7;
    }
    const created = await run(() =>
      api.createPlatform(site.id, { kind, label: kind, config: { kind, ...config } as never }),
    );
    if (created) {
      setConfigValue('');
      platforms.reload();
    }
  };

  return (
    <Card
      title={
        <div className="inline">
          <button type="button" className="ghost" onClick={() => setOpen(!open)}>
            {open ? '▾' : '▸'}
          </button>
          <h2>{site.name}</h2>
          <span className="badge mono">{site.domain}</span>
          {!site.enabled ? <span className="badge warn">désactivé</span> : null}
        </div>
      }
      actions={
        <button
          type="button"
          className="small danger"
          onClick={async () => {
            if (!confirm(`Supprimer ${site.name} et toutes ses données ?`)) return;
            await run(() => api.deleteSite(site.id));
            onChanged();
          }}
        >
          Supprimer
        </button>
      }
    >
      {error ? <Notice kind="error">{error}</Notice> : null}
      {open ? (
        <div className="stack">
          <Field
            label="Instructions LLM — niveau site (héritées par toutes les plateformes)"
            help="Ce que fait le site, pour qui, et ce qu’il n’est pas. C’est la base de tous les brouillons."
          >
            <textarea value={instructions} onChange={(event) => setInstructions(event.target.value)} />
          </Field>
          <div className="row">
            <div>
              <label>Identifiant Umami (attribution des conversions)</label>
              <input value={umamiId} onChange={(event) => setUmamiId(event.target.value)} className="mono" />
            </div>
            <div className="shrink">
              <button type="button" onClick={saveSite} disabled={busy}>
                Enregistrer
              </button>
            </div>
            <div className="shrink">
              <button
                type="button"
                disabled={busy}
                title="Re-note tout le corpus déjà collecté avec la grille actuelle. Aucune collecte, aucune alerte."
                onClick={async () => {
                  // Par défaut on ne reprend que les hits jamais notés : c'est
                  // ce que laisse derrière lui un rattrapage volumineux, et ça
                  // ne repaie pas ce qui est déjà noté.
                  const tout = confirm(
                    'OK : re-noter TOUT le corpus (coûteux).\nAnnuler : ne noter que les discussions sans note.',
                  );
                  const result = await run(() => api.rescore(site.id, { onlyUnscored: !tout }));
                  if (result) {
                    setRescored(
                      `${result.rescored} discussion(s) notée(s), dont ${result.changed} changement(s).` +
                        (result.error ? ` ${result.error}` : '') +
                        (result.skippedIntel > 0 ? ` ${result.skippedIntel} source(s) de veille laissée(s) sans note.` : ''),
                    );
                  }
                }}
              >
                Re-noter le corpus
              </button>
            </div>
          </div>
          {rescored ? <Notice kind="success">{rescored}</Notice> : null}

          <div className="row">
            <div>
              <label>Ajouter une plateforme</label>
              <select value={kind} onChange={(event) => setKind(event.target.value as PlatformKind)}>
                {ALL_KINDS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                    {MANUAL_PLATFORMS.includes(option) ? ' (rappel manuel)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>
                {kind === 'stackexchange'
                  ? 'Site Stack Exchange'
                  : kind === 'mastodon'
                    ? 'Instance'
                    : kind === 'facebook_group' || kind === 'linkedin'
                      ? 'Rappel tous les N jours'
                      : 'Aucune configuration requise'}
              </label>
              <input
                value={configValue}
                onChange={(event) => setConfigValue(event.target.value)}
                disabled={!['stackexchange', 'mastodon', 'facebook_group', 'linkedin'].includes(kind)}
                placeholder={kind === 'stackexchange' ? 'serverfault' : kind === 'mastodon' ? 'https://piaille.fr' : '7'}
              />
            </div>
            <div className="shrink">
              <button type="button" onClick={addPlatform} disabled={busy}>
                Ajouter
              </button>
            </div>
          </div>

          {MANUAL_PLATFORMS.includes(kind) ? (
            <Notice kind="rule">
              Facebook et LinkedIn n’ont pas de collecteur : leurs API sont fermées et les contourner
              exposerait au bannissement du domaine. Cette plateforme sert de rappel périodique ;
              les discussions trouvées à la main entrent par l’écran « Ajout manuel ».
            </Notice>
          ) : null}

          <div className="tree">
            {(platforms.data ?? []).map((platform) => (
              <PlatformNode key={platform.id} platform={platform} onChanged={platforms.reload} />
            ))}
            {platforms.data && platforms.data.length === 0 ? (
              <Empty>Aucune plateforme sur ce site.</Empty>
            ) : null}
          </div>
        </div>
      ) : null}
    </Card>
  );
}

function PlatformNode({ platform, onChanged }: { platform: Platform; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const sources = useAsync(() => api.sources(platform.id), [platform.id]);
  const keywords = useAsync(() => api.keywords({ platformId: platform.id }), [platform.id]);
  const { run, busy, error } = useAction();
  const [instructions, setInstructions] = useState(platform.llmInstructions ?? '');
  const [value, setValue] = useState('');
  const [term, setTerm] = useState('');
  const [matchType, setMatchType] = useState('contains');
  const [isNegative, setIsNegative] = useState(false);

  const addSource = async () => {
    const created = await run(() =>
      api.createSource(platform.id, {
        kind: (SOURCE_KIND_FOR[platform.kind] ?? 'query') as never,
        value,
      }),
    );
    if (created) {
      setValue('');
      sources.reload();
    }
  };

  const addKeyword = async () => {
    const created = await run(() =>
      api.createKeyword({ platformId: platform.id, term, matchType: matchType as never, isNegative }),
    );
    if (created) {
      setTerm('');
      keywords.reload();
    }
  };

  return (
    <div className="tree-node">
      <div className="tree-head" onClick={() => setOpen(!open)}>
        <span className="caret">{open ? '▾' : '▸'}</span>
        <strong>{platform.label}</strong>
        <span className="badge">{platform.kind}</span>
        <span className="small muted">toutes les {platform.pollIntervalMinutes} min</span>
        <span className="spacer" style={{ flex: 1 }} />
        <span className="small muted">{sources.data?.length ?? 0} source(s)</span>
      </div>

      {open ? (
        <div className="tree-children stack">
          {error ? <Notice kind="error">{error}</Notice> : null}

          <Field
            label="Instructions LLM — niveau plateforme (surchargent le site)"
            help="Ton et registre attendus sur cette plateforme."
          >
            <textarea
              value={instructions}
              onChange={(event) => setInstructions(event.target.value)}
              style={{ minHeight: 80 }}
            />
          </Field>
          <div className="inline">
            <button
              type="button"
              className="small"
              disabled={busy}
              onClick={() => run(() => api.updatePlatform(platform.id, { llmInstructions: instructions || null }))}
            >
              Enregistrer
            </button>
            <button
              type="button"
              className="small danger"
              disabled={busy}
              onClick={async () => {
                if (!confirm('Supprimer cette plateforme et ses sources ?')) return;
                await run(() => api.deletePlatform(platform.id));
                onChanged();
              }}
            >
              Supprimer la plateforme
            </button>
          </div>

          <div>
            <label>Mots-clés — préfiltre gratuit appliqué avant tout appel LLM</label>
            <div className="inline" style={{ marginBottom: 8 }}>
              {(keywords.data ?? []).map((keyword) => (
                <span key={keyword.id} className={keyword.isNegative ? 'badge danger' : 'badge ok'}>
                  {keyword.isNegative ? '− ' : '+ '}
                  {keyword.term}
                  <span className="muted">({keyword.matchType})</span>
                  <button
                    type="button"
                    className="ghost small"
                    onClick={async () => {
                      await run(() => api.deleteKeyword(keyword.id));
                      keywords.reload();
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
              {keywords.data && keywords.data.length === 0 ? (
                <span className="small muted">
                  Aucun mot-clé : sans mot-clé positif, aucun élément n’est retenu (et le LLM n’est jamais appelé).
                </span>
              ) : null}
            </div>
            <div className="row">
              <input value={term} onChange={(event) => setTerm(event.target.value)} placeholder="ssl certificate expired" />
              <select value={matchType} onChange={(event) => setMatchType(event.target.value)} style={{ maxWidth: 140 }}>
                <option value="contains">contient</option>
                <option value="word">mot entier</option>
                <option value="regex">regex</option>
              </select>
              <label className="inline shrink small" style={{ marginBottom: 0 }}>
                <input
                  type="checkbox"
                  checked={isNegative}
                  onChange={(event) => setIsNegative(event.target.checked)}
                  style={{ width: 'auto' }}
                />
                exclusion
              </label>
              <div className="shrink">
                <button type="button" className="small" onClick={addKeyword} disabled={busy || !term}>
                  Ajouter
                </button>
              </div>
            </div>
          </div>

          <div>
            <label>Sources</label>
            {(sources.data ?? []).map((source) => (
              <SourceRow key={source.id} source={source} platformKind={platform.kind} onChanged={sources.reload} />
            ))}
            <div className="row" style={{ marginTop: 8 }}>
              <input
                value={value}
                onChange={(event) => setValue(event.target.value)}
                placeholder={
                  platform.kind === 'reddit'
                    ? 'sysadmin'
                    : platform.kind === 'rss'
                      ? 'https://forum.example/feed'
                      : 'requête ou tag'
                }
              />
              <div className="shrink">
                <button type="button" className="small" onClick={addSource} disabled={busy || !value}>
                  Ajouter la source
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SourceRow({
  source,
  platformKind,
  onChanged,
}: {
  source: Source;
  platformKind: string;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [instructions, setInstructions] = useState(source.llmInstructions ?? '');
  const [outcome, setOutcome] = useState<CollectionOutcome | null>(null);
  const [since, setSince] = useState('2021-01-01');
  const runs = useAsync(() => api.pollRuns(source.id), [source.id, outcome]);
  const { run, busy, error } = useAction();

  const collect = async () => {
    const result = await run(() => api.collect(source.id));
    if (result) setOutcome(result);
  };

  /**
   * `relevance` interroge le moteur de la plateforme trié par pertinence sur
   * tout l'historique — c'est le « rattrapage inversé ». Les dates sont alors
   * sans effet : on prend les meilleurs fils, pas ceux d'une fenêtre.
   */
  const backfill = async (mode: 'chronological' | 'relevance') => {
    const result = await run(() =>
      api.backfill(source.id, {
        ...(mode === 'chronological' ? { since: new Date(since).toISOString() } : {}),
        maxItems: 100,
        mode,
      }),
    );
    if (result) setOutcome(result);
  };

  return (
    <div className="card" style={{ marginBottom: 8 }}>
      <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => setOpen(!open)}>
        <span className="caret">{open ? '▾' : '▸'}</span>
        <span className="mono">{source.value}</span>
        <span className="badge">{source.kind}</span>
        {source.purpose === 'intel' ? <span className="badge accent">veille</span> : null}
        {!source.enabled ? <span className="badge warn">désactivée</span> : null}
        <span className="spacer" />
        <span className="small muted">
          {source.lastPolledAt ? `collectée ${formatDate(source.lastPolledAt)}` : 'jamais collectée'}
        </span>
      </div>

      {open ? (
        <div className="card-body stack">
          {error ? <Notice kind="error">{error}</Notice> : null}
          {outcome ? (
            <Notice kind={outcome.status === 'ok' ? 'success' : 'error'}>
              {outcome.itemsFetched} élément(s) récupéré(s), {outcome.itemsKept} retenu(s),{' '}
              {outcome.itemsNew} nouveau(x). Statut : {outcome.status}
              {outcome.error ? ` — ${outcome.error}` : ''}
            </Notice>
          ) : null}

          <Field label="Instructions LLM — niveau source (les plus spécifiques, elles l’emportent)">
            <textarea
              value={instructions}
              onChange={(event) => setInstructions(event.target.value)}
              style={{ minHeight: 70 }}
            />
          </Field>

          <div className="row">
            <div>
              <label>Objectif de la source</label>
              <select
                value={source.purpose}
                disabled={busy}
                onChange={async (event) => {
                  await run(() =>
                    api.updateSource(source.id, {
                      purpose: event.target.value as 'prospect' | 'intel',
                    }),
                  );
                  onChanged();
                }}
              >
                <option value="prospect">Prospection — alimente l’inbox, scorée, peut alerter</option>
                <option value="intel">Veille concurrentielle — analyse seulement, pas de scoring</option>
              </select>
              <div className="help">
                Une source de veille ne coûte aucun appel de scoring : ses discussions ne servent
                qu’à l’écran Concurrence.
              </div>
            </div>
          </div>

          <div className="inline">
            <button
              type="button"
              className="small"
              disabled={busy}
              onClick={() => run(() => api.updateSource(source.id, { llmInstructions: instructions || null }))}
            >
              Enregistrer
            </button>
            <button type="button" className="small" onClick={collect} disabled={busy}>
              Collecter maintenant
            </button>
            <button
              type="button"
              className="small"
              disabled={busy}
              onClick={() => run(() => api.updateSource(source.id, { enabled: !source.enabled })).then(onChanged)}
            >
              {source.enabled ? 'Désactiver' : 'Activer'}
            </button>
            <button
              type="button"
              className="small danger"
              disabled={busy}
              onClick={async () => {
                if (!confirm('Supprimer cette source et ses hits ?')) return;
                await run(() => api.deleteSource(source.id));
                onChanged();
              }}
            >
              Supprimer
            </button>
          </div>

          {BACKFILL_CAPABLE.includes(platformKind) ? (
            <div className="notice">
              <strong>Rattrapage historique</strong>
              <div className="small muted" style={{ margin: '4px 0 8px' }}>
                Remonte les discussions antérieures à la mise en surveillance. Les vieux fils bien
                classés dans Google rapportent du trafic pendant des années. La dédup s’applique,
                et le rattrapage n’envoie pas d’alerte.
              </div>
              <div className="row" style={{ alignItems: 'flex-end' }}>
                <div className="shrink">
                  <button
                    type="button"
                    className="primary small"
                    onClick={() => backfill('relevance')}
                    disabled={busy}
                    title="Interroge le moteur de la plateforme trié par pertinence, sur tout l’historique. Les dates sont ignorées."
                  >
                    {busy ? 'En cours…' : 'Les meilleurs fils de tous les temps'}
                  </button>
                </div>
                <div>
                  <label>ou depuis une date</label>
                  <input type="date" value={since} onChange={(event) => setSince(event.target.value)} />
                </div>
                <div className="shrink">
                  <button type="button" className="small" onClick={() => backfill('chronological')} disabled={busy}>
                    Balayer la période
                  </button>
                </div>
              </div>
              <div className="small muted" style={{ marginTop: 6 }}>
                Le premier bouton demande à l’index de la plateforme <em>quels fils comptent</em> ;
                le second balaie une fenêtre en espérant tomber dessus. Préférez le premier.
              </div>
            </div>
          ) : (
            <Notice kind="rule">
              Pas de rattrapage possible sur cette plateforme : aucune API ne l’expose légalement.
              Utilisez l’ajout manuel pour injecter un fil trouvé à la main.
            </Notice>
          )}

          <div>
            <label>Dernières collectes</label>
            <table>
              <thead>
                <tr>
                  <th>Début</th>
                  <th className="num">Récupérés</th>
                  <th className="num">Retenus</th>
                  <th className="num">Nouveaux</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {(runs.data ?? []).slice(0, 5).map((pollRun) => (
                  <tr key={pollRun.id}>
                    <td className="small">{formatDate(pollRun.startedAt)}</td>
                    <td className="num">{pollRun.itemsFetched}</td>
                    <td className="num">{pollRun.itemsKept}</td>
                    <td className="num">{pollRun.itemsNew}</td>
                    <td>
                      <PollBadge status={pollRun.status} />
                      {pollRun.error ? <div className="small muted">{pollRun.error}</div> : null}
                    </td>
                  </tr>
                ))}
                {runs.data && runs.data.length === 0 ? (
                  <tr>
                    <td colSpan={5}>
                      <Empty>Aucune collecte pour l’instant.</Empty>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
